import { FC, useContext, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";

import { Button } from "components";
import { ASSET_DETAIL_URL } from "constants/index";
import { AppContext } from "contexts";
import { defaultErrorMessage } from "lib/axios/constants";
import { useManyAssetAdministrations } from "services/assetAdministrations";
import { createRelistRequest, useRelistRequest } from "services/relistRequests";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    subtitle: {
      fontSize: "1.2rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    alertInfo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "left",
      textAlign: "left",
      marginBottom: theme.spacing(1),
    },
  }),
);

interface ListingSectionProps {
  activeRelistRequestId: number | null;
  listingIds: number[];
  resaleInfoId: number;
  isIncompletePartialResale: boolean;
}

const ListingSection: FC<ListingSectionProps> = ({
  activeRelistRequestId,
  listingIds,
  resaleInfoId,
  isIncompletePartialResale,
}) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { user } = useContext(AppContext);
  const [isCreatingRelistRequest, setIsCreatingRelistRequest] = useState(false);
  const [relistRequestError, setRelistRequestError] = useState("");

  const assetAdministrationsQueries = useManyAssetAdministrations();
  const { data: relistRequest } = useRelistRequest(activeRelistRequestId);

  const handleCreateRelistRequest = async () => {
    setIsCreatingRelistRequest(true);
    setRelistRequestError("");

    try {
      await createRelistRequest({
        user: user.userId,
        resale_info: resaleInfoId,
      });
      await queryClient.cancelQueries("manyAssetAdministrations");
      await assetAdministrationsQueries.refetch();
    } catch (error) {
      setRelistRequestError(
        error instanceof Error ? error.message : defaultErrorMessage,
      );
    } finally {
      setIsCreatingRelistRequest(false);
    }
  };

  const canSeeRequestListingButton =
    !relistRequest && (!listingIds.length || isIncompletePartialResale);

  return (
    <Box>
      <Typography gutterBottom className={classes.subtitle}>
        <strong>{listingIds.length <= 1 ? "Listing" : "Listings"}</strong>
      </Typography>
      {relistRequestError && (
        <Box mb={2}>
          <Alert severity="error">
            <Typography variant="body2">{relistRequestError}</Typography>
          </Alert>
        </Box>
      )}
      {relistRequest && (
        <Box mb={2}>
          <Alert severity="info" className={classes.alertInfo}>
            <Typography style={{ whiteSpace: "pre-wrap" }}>
              {relistRequest.status_text
                ? relistRequest.status_text
                : relistRequest.state === "denied"
                ? "Your asset cannot be relisted at this time."
                : "Your listing request is currently being processed by our team."}
            </Typography>
          </Alert>
        </Box>
      )}
      {canSeeRequestListingButton && (
        <Box mb={2} width="136px">
          <Button
            fullWidth
            variant="contained"
            customColor="purple"
            onClick={handleCreateRelistRequest}
            disabled={isCreatingRelistRequest}
            isLoading={isCreatingRelistRequest}
          >
            {isCreatingRelistRequest ? "Processing" : "Request Listing"}
          </Button>
        </Box>
      )}
      {!!listingIds.length && (
        <Box display="flex" flexWrap="wrap" mb={1} gridGap={8}>
          {listingIds.map(listingId => (
            <Box key={listingId}>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={`${ASSET_DETAIL_URL}${listingId}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Listing&nbsp;#{listingId}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ListingSection;
